"use client";

import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { projects } from "@/data/projects";
import ProjectHero from "@/components/ProjectHero";
import ProjectHeroImage from "@/components/ProjectHeroImage";
import ProjectOverview from "@/components/ProjectOverview";
import NumberedSection from "@/components/NumberedSection";
import ProjectTakeaway from "@/components/ProjectTakeaway";
import ProjectOutput from "@/components/ProjectOutput";
import NextProjectFooter from "@/components/NextProjectFooter";
import ProjectSidebar, { SidebarSection } from "@/components/ProjectSidebar";

gsap.registerPlugin(ScrollTrigger);

const AcademiaDaBoaPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const projectData = projects.find((p) => p.id === "academia-da-boa");

  const currentIndex = projects.findIndex((p) => p.id === "academia-da-boa");
  const nextProject =
    currentIndex >= 0 && currentIndex < projects.length - 1
      ? projects[currentIndex + 1]
      : projects[0];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".academia-custom-section",
        { y: 24, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".academia-custom-section",
            start: "top 85%",
          },
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  if (!projectData) {
    return (
      <div className="min-h-screen bg-[#fafcfd] text-[#111111]">
        Project not found
      </div>
    );
  }

  const sidebarSections: SidebarSection[] = [
    ...(projectData.overview ? [{ id: "overview", label: "Overview" }] : []),
    ...(projectData.quote ? [{ id: "main-goal", label: "Main Goal" }] : []),
    ...(projectData.sections
      ? projectData.sections.map((s) => ({
          id: `section-${s.number}`,
          label: s.title,
        }))
      : []),
    ...(projectData.output ? [{ id: "output", label: "Output" }] : []),
    ...(projectData.takeaway ? [{ id: "takeaway", label: "Takeaway" }] : []),
  ];

  return (
    <div ref={containerRef} className="bg-[#fafcfd] text-[#111111]">
      <ProjectSidebar sections={sidebarSections} />

      <section className="relative min-h-fit md:h-[70vh] overflow-hidden bg-[#fafcfd]">
        <div className="flex items-center justify-center md:h-full">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <ProjectHero
              title={projectData.title}
              subtitle={projectData.subtitle}
              category={projectData.category}
              year={projectData.year}
              featured={projectData.featured}
              link={projectData.link}
              infoBlocks={projectData.infoBlocks || []}
            />
          </div>
        </div>
      </section>

      <ProjectHeroImage
        image={projectData.heroImage}
        alt={projectData.title + " Hero Image"}
      />

      {projectData.overview && (
        <ProjectOverview overview={projectData.overview} id="overview" />
      )}

      {projectData.quote && (
        <section id="main-goal" className="academia-custom-section py-24 border-t border-[#e8e8e8]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p className="text-sm uppercase tracking-[0.15em] text-[#6f6f6f] mb-6 font-pp-neuebit">
              main goal
            </p>
            <p className="text-[#111111] font-pp-acma text-[clamp(24px,4vw,48px)] font-semibold leading-[1.2] lowercase">
              {projectData.quote.text}
            </p>
          </div>
        </section>
      )}

      {projectData.sections &&
        projectData.sections.map((section, index) => (
          <div key={index} className="academia-custom-section">
            <NumberedSection
              number={section.number}
              title={section.title}
              content={section.content}
              largeImage={section.largeImage}
              smallImages={section.smallImages}
              id={`section-${section.number}`}
            />
          </div>
        ))}

      {projectData.output && (
        <ProjectOutput stats={projectData.output} id="output" />
      )}

      {projectData.takeaway && (
        <ProjectTakeaway takeaway={projectData.takeaway} id="takeaway" />
      )}

      <NextProjectFooter
        nextProject={
          nextProject
            ? {
                id: nextProject.id,
                title: nextProject.title,
                category: nextProject.category,
                accentColor: nextProject.accentColor,
              }
            : null
        }
      />
    </div>
  );
};

export default AcademiaDaBoaPage;
